import React from 'react'
import cx from 'classnames'
import _ from 'lodash'

import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd'

import S from './styled'
import { HamburgerGray, SmallPlus, EditIcon, CurvedArrow, DeleteIcon } from '../icons'
import { ConfirmModal } from '../modal'


// a little function to help us with reordering the result
const reorder = (list, startIndex, endIndex) => {
  const result = Array.from(list)
  const [removed] = result.splice(startIndex, 1)
  result.splice(endIndex, 0, removed)

  return result
}

const getItemStyle = (isDragging, draggableStyle) => ({
  // some basic styles to make the items look a bit nicer
  userSelect: 'none',
  outline: 'none',
  background: isDragging ? 'transparent' : 'white',
  padding: isDragging ? '20px 0' : 0,
  marginTop: isDragging ? '-20px' : 0,

  // styles we need to apply on draggables
  ...draggableStyle,
})

const getListStyle = isDraggingOver => ({
  padding: isDraggingOver ? '20px' : 0,
  border: isDraggingOver ? '1px dashed #ddd' : 'none',
  borderRadius: isDraggingOver ? '6px' : 'none',
  transition: 'padding .1s ease-in-out',
})

class List extends React.Component {

  state = {
    itemToDelete: null,
  }

  setShowDeleteModal = deleteInfo => this.setState({ itemToDelete: deleteInfo })

  onDragEnd = (result, category, categoryIndex, subcategory) => {
    // dropped outside the list
    if (!result.destination) {
      return
    }

    const items = reorder(
      category[subcategory],
      result.source.index,
      result.destination.index,
    )
    this.props.onChangeOrder({ category, categoryIndex, items, oldIndex: result.source.index, newIndex: result.destination.index })
  }

  render() {
    const {
      name,
      subcategory,
      items = [],
      onAdd,
      onRename,
      onSelect,
      selected,
      hasSubchildrenField,
      subchildrenField,
      onDelete,
      allowAddCategory,
      onAddCategory,
      options,
      allowDeleteCategory,
      modelPrefix,
      getConfirmDeleteText,
      readOnly,
    } = this.props

    const prefix = (rootIndex, itemIndex) => `${name}[${rootIndex}].${subcategory}[${itemIndex}]`
    const subprefix = (rootIndex, itemIndex, subitemIndex) => `${name}[${rootIndex}].${subcategory}[${itemIndex}].${subchildrenField}[${subitemIndex}]`

    return (
      <S.List>
        {
          items.map((category, categoryIndex) => (
            <React.Fragment key={`${options.getCategoryTitle(category)}`}>
              <S.ListItem className="category">
                {options.getCategoryTitle(category)} {
                  !readOnly && onRename && (<span className="icon edit"><EditIcon
                    onClick={() => onRename({
                      field: categoryIndex,
                      translates: options.getCategoryTitleField(category),
                    })}
                  /></span>)
                }
                {
                  !readOnly && allowDeleteCategory && <S.Delete onClick={() =>
                    this.setShowDeleteModal({
                      categoryIndex: categoryIndex,
                      category,
                      deleteCategory: true,
                    })
                  }>
                    <DeleteIcon />
                  </S.Delete>
                }
              </S.ListItem>
              <DragDropContext onDragEnd={result => this.onDragEnd(result, category, categoryIndex, subcategory)}>
                <Droppable droppableId={`droppable_${categoryIndex}`} isDropDisabled={readOnly}>
                  {(provided, snapshot) => (
                    <div
                      {...provided.droppableProps}
                      ref={provided.innerRef}
                      style={getListStyle(snapshot.isDraggingOver)}
                    >
                      {
                        (category[subcategory] || []).map((item, index) => (
                          <Draggable
                            key={`item_${options.getId(item)}`}
                            draggableId={`item_${options.getId(item)}`}
                            index={index}
                            isDragDisabled={readOnly}
                          >
                            {(provided, snapshot) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                style={getItemStyle(
                                  snapshot.isDragging,
                                  provided.draggableProps.style,
                                )}
                              >
                                <S.ListItem
                                  className={cx({
                                    subcategory: true,
                                    selected: prefix(categoryIndex, index) === selected,
                                    error: !options.isValidPath(`${modelPrefix}.${prefix(categoryIndex, index)}`),
                                  })}
                                  onClick={() => {
                                    onSelect && onSelect(prefix(categoryIndex, index))
                                  }}
                                >
                                  <HamburgerGray />
                                  <S.ItemInfo>
                                    <S.ItemInfoTitle>
                                      {options.getItemTitle(item)}
                                    </S.ItemInfoTitle>
                                    <S.ItemInfoDescription>
                                      {options.getItemDescription(item)}
                                    </S.ItemInfoDescription>
                                  </S.ItemInfo>
                                  {
                                    !readOnly && prefix(categoryIndex, index) === selected && (
                                      <S.Delete onClick={() =>
                                        this.setShowDeleteModal({
                                          item,
                                          index,
                                          categoryIndex: categoryIndex,
                                          category,
                                        })
                                      }>
                                        <DeleteIcon />
                                      </S.Delete>
                                    )
                                  }
                                </S.ListItem>
                                {
                                  hasSubchildrenField && _.get(item, hasSubchildrenField) && (
                                    <React.Fragment>
                                      {
                                        _.get(item, subchildrenField, []).map((subitem, subitemIndex) => (
                                          <S.ListItem
                                            className={cx({
                                              subcategory: true,
                                              subitem: true,
                                              selected: subprefix(categoryIndex, index, subitemIndex) === selected,
                                              error: !options.isValidPath(`${modelPrefix}.${subprefix(categoryIndex, index, subitemIndex)}`),
                                            })}
                                            onClick={() => {
                                              onSelect && onSelect(subprefix(categoryIndex, index, subitemIndex))
                                            }}
                                            key={`${options.getId(item)}_${options.getId(subitem)}`}
                                          >
                                            <CurvedArrow />
                                            <S.ItemInfo>
                                              <S.ItemInfoTitle>
                                                {options.getItemTitle(subitem)}
                                              </S.ItemInfoTitle>
                                              <S.ItemInfoDescription>
                                                {options.getItemDescription(subitem)}
                                              </S.ItemInfoDescription>
                                            </S.ItemInfo>
                                            {
                                              !readOnly && subprefix(categoryIndex, index, subitemIndex) === selected && (
                                                <S.Delete onClick={() =>
                                                  this.setShowDeleteModal({
                                                    item,
                                                    index,
                                                    subitem,
                                                    subitemIndex,
                                                    categoryIndex: categoryIndex,
                                                    category,
                                                  })
                                                }>
                                                  <DeleteIcon />
                                                </S.Delete>
                                              )
                                            }
                                          </S.ListItem>
                                        ))
                                      }
                                      {
                                        !readOnly && (
                                          <S.ListItem
                                            className="addIcon subchildren"
                                            onClick={() => onAdd(category, item, index)}
                                          >
                                            <S.AddSubchild>
                                              <CurvedArrow />
                                              <SmallPlus className="icon"/>
                                            </S.AddSubchild>
                                          </S.ListItem>
                                        )
                                      }
                                    </React.Fragment>
                                  )
                                }
                              </div>
                            )
                            }

                          </Draggable>
                        ))
                      }
                      {provided.placeholder}
                    </div>)}</Droppable></DragDropContext>
              {!readOnly && (
                <S.ListItem className="addIcon" onClick={() => onAdd(category)}>
                  <SmallPlus className="icon"/>
                </S.ListItem>
              )}
            </React.Fragment>
          ))
        }

        {
          !readOnly && allowAddCategory && (
            <S.ListItem className="addIcon addCategory" onClick={onAddCategory}>
              <SmallPlus className="icon"/> Add category
            </S.ListItem>
          )
        }

        {
          !!this.state.itemToDelete && (
            <ConfirmModal
              isOpened
              onClose={() => this.setShowDeleteModal(null)}
              onConfirm={() => {
                onDelete && onDelete(this.state.itemToDelete)
                this.setShowDeleteModal(null)
              }}
              redConfirm
            >
              {
                getConfirmDeleteText ? getConfirmDeleteText(
                  this.state.itemToDelete,
                ) : 'Are you sure you want to delete this item?'
              }
            </ConfirmModal>
          )
        }
      </S.List>
    )
  }
}

export default List
